/**
 * FERP Annotation Normalizer (DRR-062)
 *
 * Normalizes Annotation entities into NormalizedEntity<Annotation>
 * for the cross-cutting contextual annotation system.
 *
 * Annotations are NOT primitives — they are cross-cutting metadata
 * that attaches to any of the 25 primitives. Safety level is inherited
 * from the annotation type (Escalation = CRITICAL, Decision = HIGH, etc.).
 */

import { SafetyLevel } from '@/types/graphql-compat';

import type { NormalizedEntity } from '@/types/normalized-entity';
import { UnifiedStatus } from '@/types/normalized-entity';

// ============================================================================
// LOCAL TYPE (resolves to generated type after codegen)
// ============================================================================

/**
 * Annotation type matching backend GqlAnnotation.
 * Once codegen runs, import from '@/generated/graphql' instead.
 */
export interface AnnotationEntity {
  __typename?: 'GqlAnnotation';
  id: string;
  tenant: string;
  entityType: string;
  entityId: string;
  parentId?: string | null;
  body: string;
  mentions: string[];
  visibility: string;
  annotationType: string;
  threadRootId?: string | null;
  replyCount: number;
  depth: number;
  workflowInstanceId?: string | null;
  safetyLevel?: string | null;
  relatedEntityIds: string[];
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  version: number;
  isActive: boolean;
  archived: boolean;
  archivedReason?: string | null;
}

// ============================================================================
// SAFETY MAPPING
// ============================================================================

/**
 * Map annotation type to SafetyLevel.
 *
 * Escalations are life-safety relevant (patient safety, financial fraud).
 * Decisions are high-impact (approval outcomes).
 * Notes/Comments are standard operations.
 */
function mapAnnotationTypeToSafetyLevel(annotationType: string): SafetyLevel {
  switch (annotationType) {
    case 'ESCALATION':
      return SafetyLevel.CRITICAL;
    case 'DECISION':
      return SafetyLevel.HIGH;
    case 'NOTE':
      return SafetyLevel.MEDIUM;
    case 'COMMENT':
    default:
      return SafetyLevel.STANDARD;
  }
}

// ============================================================================
// NORMALIZER
// ============================================================================

/**
 * Normalize an Annotation into NormalizedEntity<AnnotationEntity>
 *
 * @param annotation - The Annotation from GraphQL
 * @returns Fully typed NormalizedEntity with Annotation preserved in domainData
 *
 * @example
 * ```typescript
 * const normalized = normalizeAnnotation(annotation);
 * console.log(normalized.displayName);           // "Patient needs follow-up..."
 * console.log(normalized.sourceType);            // "Annotation"
 * console.log(normalized.domainData.body);       // Full body text
 * console.log(normalized.domainData.replyCount); // 3
 * ```
 */
export function normalizeAnnotation(
  annotation: AnnotationEntity,
): NormalizedEntity<AnnotationEntity> {
  const safetyLevel = annotation.safetyLevel
    ? mapAnnotationTypeToSafetyLevel(annotation.safetyLevel)
    : mapAnnotationTypeToSafetyLevel(annotation.annotationType);

  const unifiedStatus = annotation.archived
    ? UnifiedStatus.CANCELLED
    : annotation.isActive
      ? UnifiedStatus.ACTIVE
      : UnifiedStatus.CANCELLED;

  // Truncate body for display name (first line, max 80 chars)
  const firstLine = annotation.body.split('\n')[0] ?? '';
  const displayName =
    firstLine.length > 80 ? `${firstLine.substring(0, 77)}...` : firstLine;

  return {
    id: annotation.id,
    displayName: displayName || `Annotation ${annotation.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Annotation',
    safetyLevel,
    requiresDualApproval: false, // Annotations never require dual approval
    complianceFrameworks: [], // Inherited from parent entity
    unifiedStatus,
    isBlockedBySafety: false, // Annotations are never safety-blocked
    domainData: annotation,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// TYPE GUARD
// ============================================================================

/**
 * Type guard for Annotation entities.
 *
 * Checks for __typename === 'GqlAnnotation' or structural match
 * (has body, entityType, entityId, annotationType fields).
 */
export function isAnnotation(obj: unknown): obj is AnnotationEntity {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    (('__typename' in obj &&
      (obj as { __typename: string }).__typename === 'GqlAnnotation') ||
      ('body' in obj &&
        'entityType' in obj &&
        'entityId' in obj &&
        'annotationType' in obj &&
        'replyCount' in obj))
  );
}
